import ReportImagePicker from '@components/Customization/ReportImagePicker';
import { isImageGone, storedImageStatus } from '@components/Customization/reportImageStatus';
import { useLocalization } from '@lib/localization';
import { Box } from '@mui/material';
import { inheritedLogoStyles } from '@pages/ReportBranding.styles';
import { imageBoxStyles } from '@pages/ReportDesigns.styles';
import { useGetReportBrandingLogoQuery, type ReportBrandingScope } from '@store/api/reportBrandingEndpoints';
import { type ReportImageRef } from '@store/api/reportDesignEndpoints';
import { useEffect } from 'react';
import FieldOrigin from './FieldOrigin';

interface Props {
  /** Whose layer the logo belongs to: a logo is stored under its layer's own owner. */
  scope: ReportBrandingScope;
  /** The logo as this layer holds it, the unsaved draft included. */
  value: ReportImageRef | undefined;
  /** The organization's logo, under a team's layer: what is printed while the team sets none. */
  organizationLogo: ReportImageRef | undefined;
  /** The files picked during this visit, as addresses a picture can show, by the id each was stored under. */
  local: Readonly<Record<string, string>>;
  /** The ids the server has answered for with "no such image". */
  missing: ReadonlySet<string>;
  pending: boolean;
  disabled: boolean;
  /** Stores a file and puts it in the draft. */
  onPick: (file: File) => Promise<unknown>;
  onRemove: () => void;
  reportMissing: (imageId: string) => void;
}

/**
 * The logo of a brand. A file is sent the moment it is picked and the draft is given its id;
 * the layer names it only once the draft is saved. Until then the picture on screen is the
 * picked file itself, and on a later visit it is read from the server.
 *
 * A team's layer that sets none shows the organization's in its box, dimmed: that is the
 * logo the team's reports are printed with.
 */
const BrandLogoField: React.FC<Props> = ({
  scope, value, organizationLogo, local, missing, pending, disabled, onPick, onRemove, reportMissing,
}) => {
  const { t } = useLocalization();

  const imageId = value?.imageId;
  const picked = imageId ? local[imageId] : undefined;

  // `currentData`, not `data`: after the logo is replaced, or the layer changed, `data` is
  // still the one that was shown before.
  const { currentData: stored, error, isFetching } = useGetReportBrandingLogoQuery(
    { ...scope, imageId: imageId ?? '' },
    { skip: !imageId || Boolean(picked) },
  );

  const inherited = scope.teamId === null ? undefined : organizationLogo;
  const { currentData: inheritedPicture } = useGetReportBrandingLogoQuery(
    { teamId: null, imageId: inherited?.imageId ?? '' },
    { skip: Boolean(imageId) || !inherited },
  );

  const notFound = Boolean(imageId) && isImageGone(error);

  // Told to the page, which says it over the fields and holds the preview back.
  useEffect(() => {
    if (imageId && notFound) reportMissing(imageId);
  }, [imageId, notFound, reportMissing]);

  const label = t('ReportDesign_Logo');
  const picture = picked ?? stored;
  const gone = notFound || Boolean(imageId && missing.has(imageId));

  return (
    <div>
      <ReportImagePicker
        label={label}
        status={storedImageStatus({ imageId, picture, gone, reading: isFetching, error })}
        picture={picture}
        empty={inheritedPicture
          ? <Box sx={[imageBoxStyles, inheritedLogoStyles]}><img src={inheritedPicture} alt={label} /></Box>
          : undefined}
        onPick={onPick}
        onRemove={onRemove}
        disabled={disabled}
      />
      {/* No arrow of its own: Remove beside the picture is what takes the logo off this layer.
          And what is printed without one is said only while there is none: a picture has no
          words to put beside "set here". */}
      <FieldOrigin
        setHere={Boolean(imageId)}
        pending={pending}
        beneathSource={inherited ? 'organization' : 'builtIn'}
        beneath={imageId ? undefined : t(inherited ? 'ReportBranding_Logo_Organization' : 'ReportBranding_Logo_BuiltIn')}
      />
    </div>
  );
};

export default BrandLogoField;
